import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField
} from '@mui/material';
import { Link, useParams } from 'react-router-dom';
import { Analytics } from '@mui/icons-material';
import axios from 'axios';
import { API_URL } from '../constants';

const TOKOverview = () => {
  const { projectKey } = useParams();
  const [subpages, setSubpages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRetailer, setSelectedRetailer] = useState('Snusbolaget');
  const [selectedDate, setSelectedDate] = useState('2025-08-08'); // Only available date

  // Fetch available subpages
  useEffect(() => {
    const fetchSubpages = async () => {
      try {
        setLoading(true);
        // This would be replaced with actual API endpoint to get subpage list
        const response = await axios.get(`${API_URL}/toc-kpi/subpages`);
        setSubpages(response.data.subpages || []);
      } catch (err) {
        console.error('Error fetching subpages:', err);
        setError('Failed to load subpages');
      } finally {
        setLoading(false);
      }
    };

    fetchSubpages();
  }, []);

  if (loading) {
    return (
      <Box sx={{ width: '100%', maxWidth: '1400px', px: 3, py: 2 }}>
        <Box sx={{ mb: 3 }}>
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 700,
              color: 'text.primary',
              mb: 0.5,
              letterSpacing: '-0.02em',
              fontSize: { xs: '1.75rem', md: '2rem' }
            }}
          >
            Total Ownership KPI - Overview
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'text.secondary',
              mt: 0.5
            }}
          >
            Analyze Total Ownership Coefficient (TOC) metrics across all subpages
          </Typography>
        </Box>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ width: '100%', maxWidth: '1400px', px: 3, py: 2 }}>
        <Box sx={{ mb: 3 }}>
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 700,
              color: 'text.primary',
              mb: 0.5,
              letterSpacing: '-0.02em',
              fontSize: { xs: '1.75rem', md: '2rem' }
            }}
          >
            Total Ownership KPI - Overview
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'text.secondary',
              mt: 0.5
            }}
          >
            Analyze Total Ownership Coefficient (TOC) metrics across all subpages
          </Typography>
        </Box>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: '1400px', px: 3, py: 2 }}>
      {/* Header - Matching TOCView styling */}
      <Box sx={{ mb: 3 }}>
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 700,
            color: 'text.primary',
            mb: 0.5,
            letterSpacing: '-0.02em',
            fontSize: { xs: '1.75rem', md: '2rem' }
          }}
        >
          Total Ownership KPI - Overview
        </Typography>
        <Typography 
          variant="body2" 
          sx={{ 
            color: 'text.secondary',
            mt: 0.5
          }}
        >
          Analyze Total Ownership Coefficient (TOC) metrics across all subpages
        </Typography>
      </Box>

      {/* Filters Row: Retailer and Date */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="retailer-select-label">Retailer</InputLabel>
          <Select
            labelId="retailer-select-label"
            value={selectedRetailer}
            onChange={(e) => setSelectedRetailer(e.target.value)}
            label="Retailer"
          >
            <MenuItem value="Snusbolaget">Snusbolaget</MenuItem>
          </Select>
        </FormControl>
        
        <TextField
          type="date"
          label="Date"
          InputLabelProps={{ shrink: true }}
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          inputProps={{
            min: '2025-08-08',
            max: '2025-08-08'
          }}
          size="small"
          sx={{ minWidth: 180 }}
        />
      </Box>

      {/* Subpages Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Available Subpages ({subpages.length})
          </Typography>
          
          {subpages.length === 0 ? (
            <Box textAlign="center" py={4}>
              <Typography color="text.secondary">
                No subpages found.
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Filename</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Brand</TableCell>
                    <TableCell align="right">Products</TableCell>
                    <TableCell align="right">Total Items</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {subpages.map((subpage, index) => (
                    <TableRow key={index} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {subpage.filename}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {subpage.category ? (
                          <Chip 
                            label={subpage.category} 
                            size="small" 
                            color="primary" 
                            variant="outlined"
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            N/A
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {subpage.brand ? (
                          <Chip 
                            label={subpage.brand} 
                            size="small" 
                            color="secondary" 
                            variant="outlined"
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            N/A
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {subpage.total_products?.toLocaleString() || 0}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="medium">
                          {(subpage.total_products || 0).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          component={Link}
                          to={`/${projectKey}/toc-kpi/subpage/${subpage.id}`}
                          variant="contained"
                          size="small"
                          startIcon={<Analytics />}
                        >
                          Analyze
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      {/*
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Quick Actions
          </Typography>
          <Grid container spacing={2}>
            <Grid item>
              <Button
                variant="contained"
                color="primary"
                component={Link}
                to="/jti/toc-kpi/compare"
              >
                Compare Subpages
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="outlined"
                component={Link}
                to="/jti/toc-kpi/export"
              >
                Export Data
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="outlined"
                component={Link}
                to="/jti/toc-kpi/reports"
              >
                Generate Reports
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      */}
    </Box>
  );
};

export default TOKOverview; 